import { LanCode } from "@/store/reducers/setting";
import {
  LOCALE_TO_HREFLANG,
  LOCALE_TO_URL_SEGMENT,
  SEO_CONSTANTS,
  SUPPORTED_LOCALES,
} from "./constants";

/** next-i18next `locales` — URL 로케일 세그먼트. */
const URL_LOCALES = Object.values(LOCALE_TO_URL_SEGMENT);

/**
 * 영어(next-i18next `defaultLocale`)는 **로케일 없는 주소가 정본**이다 — `/en/x/` 가 아니라 `/x/`.
 *
 * 2026-10-29 Search Console 실측: 구글이 색인해 둔 영어 페이지는 로케일 없는 주소다(`/` 색인됨 · `/en/` 미색인).
 * 영어 정본을 `/en/` 으로 두면 영어 전체의 대표 주소가 옮겨 가 재색인을 탄다 — 사이트맵 `<loc>` 도 로케일 없는
 * 주소다. 다른 언어는 `/{로케일}/x/` 그대로.
 */
const DEFAULT_URL_LOCALE = "en";

/** URL 앞에 붙는 로케일 경로 — 영어는 빈 문자열. */
function localePrefix(segment: string): string {
  return segment === DEFAULT_URL_LOCALE ? "" : `/${segment}`;
}

/**
 * `router.asPath` → 로케일 · 쿼리 · 해시를 뗀 경로(끝 슬래시 포함).
 *
 * ⚠️ 로케일은 **세그먼트 단위**로만 뗀다. 종전 `/^\/(en|ko|zh|ja|de|fr|es)/` 는 경계가 없어 `/dev/price-list` 의
 * `/de` 를 떼 `/v/price-list` 가 됐다.
 */
export function pathWithoutLocale(asPath: string): string {
  const path = asPath.split(/[?#]/)[0] || "/";
  const m = path.match(/^\/([^/]+)(\/.*)?$/);
  const rest = m && URL_LOCALES.includes(m[1]) ? m[2] || "/" : path;
  return withTrailingSlash(rest);
}

/** `trailingSlash: true` 라 슬래시 없는 절대 URL 은 308 을 한 번 더 탄다(`docs/pitfalls.md` #60 끝슬래시). */
export function withTrailingSlash(path: string): string {
  return path.endsWith("/") ? path : `${path}/`;
}

/**
 * 페이지의 canonical · hreflang 주소 — 사이트맵(`next-sitemap.config.js` alternateRefs)과 같은 모양.
 *
 * ```
 *   canonical   https://www.bhaptics.com/{현재 로케일}{경로}/      ← 쿼리 · 해시 없음 · 영어는 로케일 없이
 *   hreflang    https://www.bhaptics.com/{ko|ja|zh|…}{경로}/        ← en 은 로케일 없이
 *   x-default   https://www.bhaptics.com{경로}/                    ← en 과 같은 주소
 * ```
 *
 * ⚠️ 종전 `Header` 는 canonical 을 `BASE + router.asPath` 로 만들었다 — asPath 는 **로케일이 빠지고 쿼리가 붙은**
 * 값이라 `/ko/games/pc/` 의 canonical 이 `https://www.bhaptics.com/games/pc/`(기본 언어 페이지)였고
 * `?utm_source=` 도 그대로 붙었다(2026-10-29 prod 실측). 한국어 페이지를 「기본 언어의 사본」으로 신고한 셈이다.
 *
 * @param languageCode 현재 URL 로케일(`ko` · `ja` …) — `useLocale().languageCode`
 * @param canonicalPath 페이지가 정한 canonical 경로(로케일 없이) — 없으면 현재 경로
 */
export function buildPageUrls({
  asPath,
  languageCode,
  canonicalPath,
}: {
  asPath: string;
  languageCode: string;
  canonicalPath?: string;
}) {
  const base = SEO_CONSTANTS.BASE_URL;
  const path = pathWithoutLocale(asPath);
  const canonical = `${base}${localePrefix(languageCode)}${withTrailingSlash(canonicalPath ?? path)}`;
  const alternates = SUPPORTED_LOCALES.map((lan: LanCode) => ({
    hrefLang: LOCALE_TO_HREFLANG[lan],
    href: `${base}${localePrefix(LOCALE_TO_URL_SEGMENT[lan])}${path}`,
  }));
  return { canonical, alternates, xDefault: `${base}${path}` };
}
